import React, { useState, useEffect } from 'react';
import { getTeamsByOrganization, exportProjectData } from '../../api';
import DeleteProjectDataModal from './DeleteProjectDataModal';

function ProjectExportModal({ isOpen, onClose, user }) {
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [exportedTeamId, setExportedTeamId] = useState(null);
  const [exportedTeamName, setExportedTeamName] = useState('');

  // Teams laden
  useEffect(() => {
    if (isOpen) {
      loadTeams();
    }
  }, [isOpen, user.id]);

  // Erfolgsmeldung nach 3 Sekunden ausblenden
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const loadTeams = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getTeamsByOrganization(user.id, user.organization_id);
      if (response.success) {
        setTeams(response.teams || []);
      } else {
        setError(response.error || 'Fehler beim Laden der Projekte');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!selectedTeamId) {
      setError('Bitte wählen Sie ein Projekt aus');
      return;
    }

    setExporting(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await exportProjectData(user.id, selectedTeamId);
      
      if (response.success) {
        // Excel-Datei herunterladen
        if (response.excelData) {
          // Base64-Daten dekodieren
          const binaryString = atob(response.excelData);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          
          const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          const link = document.createElement('a');
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          
          // Dateiname vom Server verwenden oder generieren
          const fileName = response.fileName || `projekt_export_${selectedTeamId}_${new Date().toISOString().split('T')[0]}.xlsx`;
          link.setAttribute('download', fileName);
          
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          
          // Erfolgsmeldung anzeigen
          setSuccessMessage(`Projektdaten erfolgreich exportiert! (${fileName})`);
          
          // Projektname für das Lösch-Modal speichern
          const exportedTeam = teams.find(t => String(t.id) === String(selectedTeamId));
          setExportedTeamId(selectedTeamId);
          setExportedTeamName(exportedTeam ? exportedTeam.name : '');
          
          // Lösch-Modal öffnen
          setShowDeleteModal(true);
        } else {
          setError('Keine Daten zum Exportieren gefunden');
        }
      } else {
        setError(response.error || 'Fehler beim Exportieren des Projekts');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      width: '100vw', 
      height: '100vh', 
      background: 'rgba(0,0,0,0.5)', 
      zIndex: 1000, 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center' 
    }}>
      <div className="modal-content" style={{ 
        background: 'white', 
        padding: '32px', 
        borderRadius: '8px', 
        minWidth: '400px', 
        maxWidth: '600px',
        width: '90%',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0 }}>Projekt exportieren</h2>
          <button 
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '24px',
              color: '#666',
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {error && (
          <div style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: '#fee',
            color: '#c33',
            borderRadius: '4px',
            border: '1px solid #fcc'
          }}>
            {error}
          </div>
        )}

        {successMessage && (
          <div style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: '#efe',
            color: '#3c3',
            borderRadius: '4px',
            border: '1px solid #cfc'
          }}>
            {successMessage}
          </div>
        )}

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Projekt auswählen *
          </label>
          {loading ? (
            <div>Projekte werden geladen...</div>
          ) : (
            <select
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                marginBottom: '16px'
              }}
            >
              <option value="">-- Projekt auswählen --</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>
                  {team.name} {team.isDefault ? '(Standard)' : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        <div style={{ 
          padding: '16px', 
          backgroundColor: '#f5f5f5', 
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: '500' }}>Es werden folgende Daten in separate Excel-Sheets exportiert:</p>
          <ul style={{ margin: '0', paddingLeft: '20px' }}>
            <li>Kunden</li>
            <li>Termine</li>
            <li>Leads</li>
            <li>PV Leads</li>
            <li>WP Leads</li>
            <li>Kommentare</li>
            <li>Anrufe</li>
            <li>Kunden-Kommentare</li>
            <li>Kunden-Anrufe</li>
          </ul>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>
            Die Excel-Datei kann direkt in Google Sheets hochgeladen werden, wobei jedes Sheet als separate Registerkarte angezeigt wird.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={exporting}
            style={{
              padding: '10px 20px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: '#fff',
              cursor: exporting ? 'not-allowed' : 'pointer',
              color: '#666'
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={handleExport}
            disabled={exporting || !selectedTeamId || loading}
            style={{
              padding: '10px 20px',
              fontSize: '16px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: exporting ? '#ccc' : '#4a6de5',
              color: 'white',
              cursor: (exporting || !selectedTeamId || loading) ? 'not-allowed' : 'pointer',
              fontWeight: '500'
            }}
          >
            {exporting ? 'Exportiere...' : 'Projekt exportieren'}
          </button>
        </div>
      </div>

      {/* Delete Project Data Modal */}
      <DeleteProjectDataModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          onClose();
        }}
        user={user}
        teamId={exportedTeamId}
        teamName={exportedTeamName}
        onProjectDeleted={() => {
          // Teams neu laden, da Projekt gelöscht wurde
          loadTeams();
          // Auswahl zurücksetzen
          setSelectedTeamId('');
        }}
      />
    </div>
  );
}

export default ProjectExportModal;

